import create from 'zustand'
import { planLimitRefusalOf } from '../data/entitlements'
import {
  listScrubEstimates,
  getScrubEstimateDetail,
  uploadScrubJob,
  getScrubJobStatus,
  getLatestScrubEstimateByRo,
  markScrubEstimateReviewed,
  markScrubEstimateQuoteSent,
  upsertScrubCalibration,
  patchScrubEstimateDetail
} from '../api/scrubApi'
import { useCenterStore } from './CenterStore'
import { adaptListItem, adaptDetail } from '../pages/adas-intelligence/adapters'
import { getFriendlyErrorMessage, getScrubFailureMessage, SCRUB_UPLOAD_RATE_LIMIT_MESSAGE } from '../utils/errorMessage'

// Read the active calibration center id without subscribing — used for scoping
// scrub-estimate GET calls to the user's current center per BE contract.
const getActiveCcid = () => useCenterStore.getState().ccId

const DEFAULT_PAGE_SIZE = 50

const JOB_TERMINAL = new Set(['COMPLETED', 'COMPLETE', 'SUCCEEDED', 'DONE', 'FAILED', 'ERROR', 'CANCELLED', 'CANCELED'])
const JOB_FAILED = new Set(['FAILED', 'ERROR', 'CANCELLED', 'CANCELED'])

const useAdasIntelligenceStore = create((set, get) => ({
  // List state
  estimates: [],
  page: 0,
  size: DEFAULT_PAGE_SIZE,
  totalElements: 0,
  totalPages: 0,
  isListLoading: false,
  listError: null,

  // Detail state (keyed by id to avoid stale data on navigation)
  detailsById: {},
  isDetailLoading: false,
  detailError: null,
  // The plan-limit refusal behind detailError, when there is one ({ code: 'RO_LOCKED', summary, requiredTier }).
  detailRefusal: null,

  // KPI totals (server-side counts, not derived from current page)
  kpiCounts: { total: 0, newCount: 0, reviewedCount: 0, quoteSentCount: 0 },
  isKpiLoading: false,

  // Filter state (persisted in store so routing back from detail preserves it)
  filters: { search: '', client: '', status: '', dateFrom: '', dateTo: '' },
  // Column sort `{ key, dir }` (keys whitelisted by the BE, ScrubEstimateSpecification); null = newest first.
  sort: null,

  setSort: (sort) => set({ sort, page: 0 }),
  setFilters: (patch) => set((state) => ({ filters: { ...state.filters, ...patch }, page: 0 })),
  setPage: (page) => set({ page }),

  // Clear just the queue filters + pagination (not the cached estimates/detail).
  // Called when leaving the queue page so navigating between the ADAS
  // Intelligence tabs starts from a clean, unfiltered list.
  resetFilters: () => set({ filters: { search: '', client: '', status: '', dateFrom: '', dateTo: '' }, sort: null, page: 0 }),

  reset: () =>
    set({
      estimates: [],
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      totalElements: 0,
      totalPages: 0,
      isListLoading: false,
      listError: null,
      detailsById: {},
      isDetailLoading: false,
      detailError: null,
      kpiCounts: { total: 0, newCount: 0, reviewedCount: 0, quoteSentCount: 0 },
      isKpiLoading: false,
      filters: { search: '', client: '', status: '', dateFrom: '', dateTo: '' },
      sort: null
    }),

  /** Fetch server-side totals for KPI cards by issuing 3 cheap list calls (size=1).
   *  Honors the active server-side filters (search → roNumber, client) so the tiles
   *  reflect the same scope as the visible queue. The status filter doesn't apply to
   *  these calls — each card explicitly sets its own status to count by. The
   *  client-side date filter isn't honored here yet (the list API has no date param).
   *
   *  newCount is derived (total − reviewed − quoteSent) instead of querying
   *  status=NEW directly because the BE's NEW filter matches only the literal
   *  status, but the queue shows several BE states (NEW, PENDING, RUNNING, QUEUED,
   *  PROCESSING, FAILED…) under the same "New" badge. The derived count matches
   *  what the user actually sees in the list. */
  loadKpiCounts: async () => {
    const { filters } = get()
    // While the user is searching by RO/VIN/Client, skip the 3-status fanout —
    // results are already narrowed by the search params, so we keep the previous
    // KPI snapshot rather than issuing extra API calls per status.
    if (filters.search?.trim()) {
      return
    }
    set({ isKpiLoading: true })
    try {
      const baseReq = { page: 0, size: 1 }
      const ccId = getActiveCcid()
      if (ccId != null) baseReq.ccId = ccId
      if (filters.client) baseReq.client = filters.client
      if (filters.dateFrom) baseReq.dateFrom = filters.dateFrom
      if (filters.dateTo) baseReq.dateTo = filters.dateTo

      // Status filter narrows the visible list to one bucket. Per CAR-3449 the KPI
      // counts must always match the list — so when status is set, only the matching
      // tile gets a count and the other two are zero.
      let total
      let reviewedCount
      let quoteSentCount
      if (filters.status === 'REVIEWED') {
        const r = await listScrubEstimates({ ...baseReq, status: 'REVIEWED' }).catch(() => ({ totalElements: 0 }))
        reviewedCount = r?.totalElements ?? 0
        total = reviewedCount
        quoteSentCount = 0
      } else if (filters.status === 'QUOTE_SENT') {
        const q = await listScrubEstimates({ ...baseReq, status: 'QUOTE_SENT' }).catch(() => ({ totalElements: 0 }))
        quoteSentCount = q?.totalElements ?? 0
        total = quoteSentCount
        reviewedCount = 0
      } else if (filters.status === 'NEW') {
        // BE's NEW filter is too narrow — derive the count the same way the page
        // computes "newCount" overall: total minus reviewed minus quote-sent.
        const [all, revRes, qsRes] = await Promise.all([
          listScrubEstimates(baseReq).catch(() => ({ totalElements: 0 })),
          listScrubEstimates({ ...baseReq, status: 'REVIEWED' }).catch(() => ({ totalElements: 0 })),
          listScrubEstimates({ ...baseReq, status: 'QUOTE_SENT' }).catch(() => ({ totalElements: 0 }))
        ])
        const overall = all?.totalElements ?? 0
        const rev = revRes?.totalElements ?? 0
        const qs = qsRes?.totalElements ?? 0
        total = Math.max(0, overall - rev - qs)
        reviewedCount = 0
        quoteSentCount = 0
      } else {
        const [all, revRes, qsRes] = await Promise.all([
          listScrubEstimates(baseReq).catch(() => ({ totalElements: 0 })),
          listScrubEstimates({ ...baseReq, status: 'REVIEWED' }).catch(() => ({ totalElements: 0 })),
          listScrubEstimates({ ...baseReq, status: 'QUOTE_SENT' }).catch(() => ({ totalElements: 0 }))
        ])
        total = all?.totalElements ?? 0
        reviewedCount = revRes?.totalElements ?? 0
        quoteSentCount = qsRes?.totalElements ?? 0
      }

      set({
        kpiCounts: {
          total,
          newCount: Math.max(0, total - reviewedCount - quoteSentCount),
          reviewedCount,
          quoteSentCount
        },
        isKpiLoading: false
      })
    } catch {
      set({ isKpiLoading: false })
    }
  },

  loadEstimates: async () => {
    const { page, size, filters, sort } = get()
    set({ isListLoading: true, listError: null })
    try {
      const apiParams = { page, size }
      const ccId = getActiveCcid()
      if (ccId != null) apiParams.ccId = ccId
      // The search box is a single field that may match RO / VIN / client / vehicle,
      // so we pass the same text in all four query params and let the BE OR
      // them — instead of issuing extra status-scoped calls to narrow the page.
      const q = filters.search?.trim()
      if (q) {
        apiParams.roNumber = q
        apiParams.vin = q
        apiParams.client = q
        apiParams.vehicle = q
      } else if (filters.client) {
        apiParams.client = filters.client
      }
      if (filters.dateFrom) apiParams.dateFrom = filters.dateFrom
      if (filters.dateTo) apiParams.dateTo = filters.dateTo
      if (sort) {
        apiParams.sortBy = sort.key
        apiParams.sort = sort.dir.toUpperCase()
      }
      // The BE's status=NEW filter matches only literal NEW; the FE's "New" badge
      // covers several raw BE statuses (NEW, PENDING, RUNNING, QUEUED, PROCESSING,
      // FAILED, …). Forwarding NEW to the BE drops rows the user can see in the
      // list. For the NEW case we fetch unfiltered and exclude reviewed/quote-sent
      // client-side — same derivation the KPI counts already use.
      const filterNewClientSide = filters.status === 'NEW'
      if (filters.status && !filterNewClientSide) apiParams.status = filters.status

      const data = await listScrubEstimates(apiParams)
      const content = Array.isArray(data?.content) ? data.content : []
      let adapted = content.map(adaptListItem)
      if (filterNewClientSide) {
        adapted = adapted.filter((e) => e.status !== 'reviewed' && e.status !== 'quote sent')
      }
      set({
        estimates: adapted,
        totalElements: filterNewClientSide ? adapted.length : (data?.totalElements ?? adapted.length),
        totalPages: filterNewClientSide ? 1 : (data?.totalPages ?? 1),
        isListLoading: false
      })
    } catch (err) {
      set({ isListLoading: false, listError: getFriendlyErrorMessage(err, 'Failed to load estimates') })
    }
  },

  loadDetail: async (id) => {
    if (!id) return null
    set({ isDetailLoading: true, detailError: null, detailRefusal: null })
    try {
      const dto = await getScrubEstimateDetail(id)
      const normalized = adaptDetail(dto)
      set((state) => ({
        detailsById: { ...state.detailsById, [id]: normalized },
        isDetailLoading: false
      }))
      return normalized
    } catch (err) {
      // A repair order locked by the plan (403 RO_LOCKED, 0173): the page shows the lock, not an error.
      set({
        isDetailLoading: false,
        detailError: getFriendlyErrorMessage(err, 'Failed to load estimate detail'),
        detailRefusal: planLimitRefusalOf(err)
      })
      return null
    }
  },

  getDetail: (id) => get().detailsById[id],

  /**
   * Upload a PDF, poll the job until terminal, then resolve the resulting
   * estimate id (via the RO number the job response carries).
   * Returns { estimateId, roNumber } on success, throws on failure.
   */
  uploadEstimate: async (file, { onStatus, pollIntervalMs = 1500, timeoutMs = 120000, calibrationCenterId } = {}) => {
    let job
    try {
      job = await uploadScrubJob({ file, calibrationCenterId })
    } catch (err) {
      // The backend caps uploads at 5/min per calibration center; map 429 to its
      // approved copy while preserving the original axios error for logging/context.
      if (err?.response?.status === 429) err.friendlyMessage = SCRUB_UPLOAD_RATE_LIMIT_MESSAGE
      throw err
    }
    onStatus?.({ stage: 'uploaded', job })
    if (!job?.jobId) throw new Error('Upload did not return a jobId')

    const start = Date.now()
    let current = job
    while (!JOB_TERMINAL.has(String(current.status || '').toUpperCase())) {
      if (Date.now() - start > timeoutMs) throw new Error('Scan timed out')
      await new Promise((r) => setTimeout(r, pollIntervalMs))
      current = await getScrubJobStatus(job.jobId)
      onStatus?.({ stage: 'polling', job: current })
    }
    if (JOB_FAILED.has(String(current.status || '').toUpperCase())) {
      // Terminal-but-failed jobs carry a stable failureCode (TOO_LARGE,
      // IMAGE_UNREADABLE, MAX_ATTEMPTS, GENERAL_ERROR). Surface the mapped copy;
      // friendlyMessage lets getFriendlyErrorMessage show it verbatim in the modal.
      const message = getScrubFailureMessage(current.failureCode)
      const error = new Error(message)
      error.friendlyMessage = message
      error.failureCode = current.failureCode || 'GENERAL_ERROR'
      throw error
    }

    const latest = await getLatestScrubEstimateByRo(current.roNumber, calibrationCenterId)
    onStatus?.({ stage: 'ready', estimate: latest })

    // Refresh list + KPI totals in the background so they reflect the new estimate
    get().loadEstimates()
    get().loadKpiCounts()

    return { estimateId: latest?.id, roNumber: current.roNumber }
  },

  markReviewed: async (id) => {
    const updated = await markScrubEstimateReviewed(id)
    set((state) => ({
      estimates: state.estimates.map((e) => (e.id === id ? { ...e, status: 'reviewed' } : e)),
      detailsById: {
        ...state.detailsById,
        [id]: state.detailsById[id]
          ? { ...state.detailsById[id], status: 'reviewed', rawStatus: updated?.status || 'REVIEWED' }
          : state.detailsById[id]
      }
    }))
    get().loadKpiCounts()
    return updated
  },

  // The backend bumps the estimate to a new version (new id) on each calibration
  // write, so we refetch the latest by RO and cache the detail under BOTH the old
  // id (so the current URL still resolves) and the new id.
  _refreshAfterCalibrationChange: async (id, roNumber, calibrationCenterId) => {
    const latest = await getLatestScrubEstimateByRo(roNumber, calibrationCenterId)
    const newId = latest?.estimateId || latest?.id
    if (!newId) return get().loadDetail(id)
    const dto = await getScrubEstimateDetail(newId)
    const normalized = adaptDetail(dto)
    set((state) => ({
      detailsById: {
        ...state.detailsById,
        [id]: normalized,
        [newId]: normalized
      },
      isDetailLoading: false
    }))
    return normalized
  },

  addCalibration: async (
    id,
    roNumber,
    { calibrationCenterId, displayTitle, adasCategory, calibrationMethod, performedBy, reason, productType }
  ) => {
    // productType is forwarded so the BE can route a restored finding back into its
    // original bucket (ADAS Calibrations vs Safety Operations). Omitted on plain Adds
    // from the AddCalibrationForm — the BE defaults to a calibration in that case.
    await upsertScrubCalibration(roNumber, {
      calibration_center_id: calibrationCenterId,
      action: 'ADD',
      displayTitle,
      adasCategory,
      calibrationMethod,
      performedBy,
      reason,
      ...(productType ? { productType } : {})
    })
    return get()._refreshAfterCalibrationChange(id, roNumber, calibrationCenterId)
  },

  // PATCH client_name / insurer_name on the existing detail. No version bump — the
  // returned DTO replaces the cached entry under the same id. Caller passes
  // calibrationCenterId so the API receives it alongside the patched fields.
  patchEstimateMeta: async (id, calibrationCenterId, fields) => {
    const dto = await patchScrubEstimateDetail(id, {
      calibration_center_id: calibrationCenterId,
      ...fields
    })
    const normalized = adaptDetail(dto)
    set((state) => ({
      detailsById: { ...state.detailsById, [id]: normalized }
    }))
    return normalized
  },

  removeCalibration: async (id, roNumber, { calibrationCenterId, displayTitle, performedBy, reason }) => {
    await upsertScrubCalibration(roNumber, {
      calibration_center_id: calibrationCenterId,
      action: 'REMOVE',
      displayTitle,
      performedBy,
      reason
    })
    return get()._refreshAfterCalibrationChange(id, roNumber, calibrationCenterId)
  },

  // RESTORE revives a previously-removed card to its exact prior state. The BE rehydrates
  // the original CalibrationCardDto (OEM reference, triggered-by lines, fired rule IDs,
  // confidence) from the latest REMOVE audit snapshot keyed by displayTitle — so unlike ADD
  // we only send the title/performedBy/reason; every other field comes from the snapshot.
  restoreCalibration: async (id, roNumber, { calibrationCenterId, displayTitle, performedBy, reason }) => {
    await upsertScrubCalibration(roNumber, {
      calibration_center_id: calibrationCenterId,
      action: 'RESTORE',
      displayTitle,
      performedBy,
      reason
    })
    return get()._refreshAfterCalibrationChange(id, roNumber, calibrationCenterId)
  },

  markQuoteSent: async (id) => {
    const updated = await markScrubEstimateQuoteSent(id)
    set((state) => ({
      estimates: state.estimates.map((e) => (e.id === id ? { ...e, status: 'quote sent' } : e)),
      detailsById: {
        ...state.detailsById,
        [id]: state.detailsById[id]
          ? { ...state.detailsById[id], status: 'quote sent', rawStatus: updated?.status || 'QUOTE_SENT' }
          : state.detailsById[id]
      }
    }))
    get().loadKpiCounts()
    return updated
  }
}))

export { useAdasIntelligenceStore }
export default useAdasIntelligenceStore
